import { useState } from 'react';
import { RecipientRequestStatus } from '@/types/wireRecipients';
import { SVTableRow } from '@/types/common';
import { titleCase } from '@/utils/stringCaser';
import WireRecipientManageMenu from './WireRecipientManageMenu';
import { useGetWireRecipientRequestsList } from '@/hooks/requests/useWireRecipients';
import { SVLoader, SVTable } from '@/components/core';

type WireRequestStatusTableProps = {
  customerId: string;
  status: RecipientRequestStatus;
};

export default function WireRequestStatusTable({
  customerId,
  status,
}: WireRequestStatusTableProps) {
  const needsApproval = status === 'needs approval';
  const [page, setPage] = useState(0);
  const {
    wireRecipientRequests,
    isWireRecipientRequestsPending,
    totalRecordCount,
  } = useGetWireRecipientRequestsList({
    consumer_id: customerId,
    status,
    page,
  });

  if (isWireRecipientRequestsPending) {
    return <SVLoader />;
  }

  const rows: SVTableRow[] = wireRecipientRequests.map(request => {
    const {
      uuid,
      accountName,
      recipientNickName,
      bankName,
      status,
      declinedAt,
      expirationAt,
      canceledAt,
      approvedAt,
    } = request;

    const statusTimeStamp =
      status === 'needs approval'
        ? expirationAt
        : status === 'approved'
          ? approvedAt
          : status === 'declined'
            ? declinedAt
            : status === 'expired'
              ? expirationAt
              : canceledAt;

    return {
      key: uuid,
      cells: [
        {
          content: accountName,
          type: 'text',
          key: `name${uuid}`,
        },
        {
          content: recipientNickName,
          type: 'text',
          key: `nickname${uuid}`,
        },
        {
          content: bankName,
          type: 'text',
          key: `bankName${uuid}`,
        },
        {
          content: titleCase(status),
          type: 'text',
          key: `status${uuid}`,
        },
        {
          content: statusTimeStamp || '',
          type: 'date',
          key: `date${uuid}`,
        },
        {
          content: (
            <WireRecipientManageMenu
              customerId={customerId}
              uuid={uuid}
              canCancel={needsApproval}
              name={recipientNickName}
            />
          ),
          type: 'custom',
          key: `manage${uuid}`,
        },
      ],
    };
  });

  return (
    <SVTable
      ariaLabel={`${status} Wire Recipient Requests`}
      headers={[
        { content: 'Name', key: 'name' },
        { content: 'Nickname', key: 'nickname' },
        { content: 'Bankname', key: 'bankname' },
        { content: 'Status', key: 'status' },
        { content: titleCase(status), key: 'requested' },
        { content: '', key: 'manage' },
      ]}
      rows={rows}
      hasPagination
      page={page}
      setPage={setPage}
      totalRecordCount={totalRecordCount}
    />
  );
}
